import { PaymentHandler, PayResult } from '@/store/models';
import braintree, { Client } from 'braintree-web';
import paypal from 'paypal-checkout';
import { getTotals, saveLog } from '@/store/api';
import { bus } from '@/events/bus';
import { i18n } from '@/i18n';
import checkout from '@/store/modules/checkout';
import store from '@/store';
import handleError from '@/commons/errorHandler';
import { normalizeQuoteAddress } from '@/commons/addressNormalizer';
import translit from '@/commons/translit';
import payment from '../payment';
// window.translitTest = translit;

export default class Braintree implements PaymentHandler {
    public clientInstance?: braintree.Client;
    public hostedFields?: braintree.HostedFields;
    public deviceData: any;
    public params: any;
    private authorizedData: any;

    public init(params: any) {
        this.params = params;
        return new Promise((resolve, reject) => {
            braintree.client.create({ authorization: params.token }).then((clientInstance: braintree.Client) => {
                this.clientInstance = clientInstance;
                if (params.fraud_protection) {
                    braintree.dataCollector.create({ client: clientInstance, kount: true, paypal: true })
                        .then((dcRes) => {
                            this.deviceData = dcRes.deviceData;
                        });
                }
                resolve({ api: this });
            });
        });
    }

    public renderHostedFields() {
        const options = {
            client: this.clientInstance,
            styles: this.params.clientStyles,
            fields: this.params.fields,
        };
        return braintree.hostedFields.create(options).then((hfResp) => {
            this.hostedFields = hfResp;
        });
    }

    public renderPaypal(grandTotal: number, baseCurrency: string) {
        // @ts-ignore
        braintree.paypalCheckout.create({ client: this.clientInstance })
            .then((paypalCheckoutInstance: any) => {
                paypal.Button.render({
                    env: this.params.paypalenv,
                    style: this.params.paypalStyles,
                    payment: async () => {
                        try {
                            return paypalCheckoutInstance.createPayment({
                                flow: this.params.flow,
                                intent: this.params.intent,
                                amount: grandTotal,
                                displayName: this.params.displayName,
                                currency: baseCurrency,
                            });
                        } catch (e) {
                            e();
                            return false;
                        }
                    },
                    onAuthorize: (data: any, options: any) => {
                        return paypalCheckoutInstance.tokenizePayment(data).then((payload: any) => {
                            this.params.saleCommit({success: true, data: payload});
                            checkout.flagPlacingOrder(true);
                            const payData: any = { payment_method_nonce: payload.nonce };
                            if (this.deviceData) {
                                payData.device_data = this.deviceData;
                            }

                            payment.finalize({
                                payData: { additional_data: payData },
                                paymentMethod: this.params.paypal_code,
                            });
                        });
                    },
                    onCancel: (data: any) => {
                        console.log(data);
                    },
                    onError: (err: any) => {
                        this.params.saleCommit({success: false, data: err});
                        this.params.error = err;
                        handleError(err);
                    },
                }, '#paypalButton');
            });
    }

    public pay(amount: number): Promise<PayResult> {
        return new Promise((resolve, reject) => {
            if (this.authorizedData) {
                resolve({
                    finalizeData: {
                        payData: { additional_data: this.authorizedData },
                    },
                    needsFinalization: true,
                });
            } else {
                const hostedFields = this.hostedFields as braintree.HostedFields;
                hostedFields.tokenize()
                    .then((payload) => {
                        const is3dsEnabled = store.state.options.payment.braintreeConfig.is3dsEnabled;
                        const threedsThreshold = store.state.options.payment.braintreeConfig.threedThreshold;
                        const specificCountries: string[] = store.state.options.payment.braintreeConfig.threedCountries;
                        const billingAddress = normalizeQuoteAddress(checkout.billingAddress);
                        const shippingAddress = normalizeQuoteAddress(checkout.shippingAddress);
                        const threedsEnabledForCountry = specificCountries.length === 0
                            || specificCountries.includes(billingAddress.country_id);

                        if (is3dsEnabled && threedsEnabledForCountry && amount >= threedsThreshold) {
                            braintree.threeDSecure.create({
                                version: 2,
                                client: this.clientInstance,
                            }).then((threedsInstance) => {
                                const trasnlitEnabled = store.state.options.payment.translit3ds;
                                const billingFirstname = billingAddress.firstname;
                                const billingLastname = billingAddress.lastname;
                                const options: any = {
                                    amount,
                                    nonce: payload.nonce,
                                    billingAddress: {
                                        givenName: trasnlitEnabled ? translit(billingFirstname) : billingFirstname,
                                        surname: trasnlitEnabled ? translit(billingLastname) : billingLastname,
                                        phoneNumber: billingAddress.telephone,
                                        streetAddress: billingAddress.street[0],
                                        extendedAddress: '',
                                        locality: billingAddress.city,
                                        region: billingAddress.region,
                                        postalCode: billingAddress.postcode,
                                        countryCodeAlpha2: billingAddress.country_id,
                                    },
                                    onLookupComplete: (data: any, next: any) => {
                                        next();
                                    },
                                };

                                if (payload.details) {
                                    const payloadDetails: any = payload.details;
                                    options.bin = payloadDetails.bin;
                                }

                                // if (shippingAddress) {
                                //     options.additionalInformation = {
                                //         shippingGivenName: shippingAddress.firstname,
                                //         shippingSurname: shippingAddress.lastname,
                                //         shippingPhone: shippingAddress.telephone,
                                //         shippingAddress: {
                                //             streetAddress: shippingAddress.street[0],
                                //             extendedAddress: '',
                                //             locality: shippingAddress.city,
                                //             region: shippingAddress.region,
                                //             postalCode: shippingAddress.postcode,
                                //             countryCodeAlpha2: shippingAddress.country_id,
                                //         },
                                //     };
                                // }

                                threedsInstance.verifyCard(options, (threedsErr, threedsPayload: any) => {
                                    if (threedsErr) {
                                        saveLog('3DS FAIL ' + JSON.stringify(threedsErr));
                                        reject(threedsErr.message);
                                        return;
                                    }
                                    if (threedsPayload.liabilityShifted
                                        || !threedsPayload.liabilityShifted
                                        && !threedsPayload.liabilityShiftPossible
                                    ) {
                                        const resolveArg: any = { payment_method_nonce: threedsPayload.nonce };
                                        if (this.deviceData) {
                                            resolveArg.device_data = this.deviceData;
                                        }
                                        this.authorizedData = resolveArg;
                                        resolve({
                                            finalizeData: {
                                                payData: { additional_data: resolveArg },
                                            },
                                            needsFinalization: true,
                                        });
                                    } else {
                                        // const errtolog = {
                                        //     threedsPayload,
                                        //     threedsOptions: options,
                                        //     trasnlitEnabled,
                                        //     email: checkout.email,
                                        // };
                                        // saveLog('3DS FAIL (liability) ' + JSON.stringify(errtolog));
                                        reject(i18n.t('Please try again with another form of payment').toString());
                                    }
                                });
                            })
                            .catch((e) => {
                                reject(i18n.t('Card issuer authentication failed').toString());
                            });
                        } else { // 3DS is not available
                            const resolveArg: any = { payment_method_nonce: payload.nonce };
                            if (this.deviceData) {
                                resolveArg.device_data = this.deviceData;
                            }
                            this.authorizedData = resolveArg;
                            resolve({
                                finalizeData: {
                                    payData: { additional_data: resolveArg },
                                },
                                needsFinalization: true,
                            });
                        }
                    })
                    .catch((e) => {
                        console.log(e);
                        let errmess = e.message;
                        switch (e.code) {
                            case 'HOSTED_FIELDS_FIELDS_EMPTY':
                                errmess = i18n.t('Empty credit card fields').toString();
                                break;
                        }
                        reject(errmess);
                    });
                }
        });
    }
    public teardown(params: any) {
        return new Promise((resolve, reject) => {
            if (this.hostedFields) {
                this.hostedFields.teardown((e) => {
                    if (e) {
                        console.error('Could not tear down Hosted Fields!');
                        reject(e);
                    } else {
                        resolve(true);
                    }
                });
            } else {
                resolve(false);
            }
        });
    }
}
